import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

// Função para obter cor baseada no percentual
const getPercentualColor = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'default';
  if (valor >= 100) return 'success';
  if (valor >= 81) return 'warning';
  return 'error';
};

// Função para obter cor de fundo da célula
const getCellBackgroundColor = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return 'transparent';
  if (valor >= 100) return '#c8e6c9'; // verde claro
  if (valor >= 81) return '#fff9c4'; // amarelo claro
  return '#ffcdd2'; // vermelho claro
};



// Função para calcular os meses baseado no período
const calcularMeses = (periodoSelecionado) => {
  const hoje = new Date();
  const mesAtual = startOfMonth(hoje);
  
  if (!periodoSelecionado) {
    // Se não houver período selecionado, retorna apenas o mês atual
    return [mesAtual];
  }

  let meses = [];
  
  switch (periodoSelecionado) {
    case 'ultimo-mes':
      // Último mês: apenas o mês anterior
      meses = [subMonths(mesAtual, 1)];
      break;
    case 'trimestral':
      // Trimestral: últimos 3 meses (não incluindo o mês atual)
      meses = [
        subMonths(mesAtual, 3),
        subMonths(mesAtual, 2),
        subMonths(mesAtual, 1)
      ];
      break;
    case 'semestral':
      // Semestral: últimos 6 meses (não incluindo o mês atual)
      meses = [
        subMonths(mesAtual, 6),
        subMonths(mesAtual, 5),
        subMonths(mesAtual, 4),
        subMonths(mesAtual, 3),
        subMonths(mesAtual, 2),
        subMonths(mesAtual, 1)
      ];
      break;
    default:
      meses = [mesAtual];
  }
  
  return meses;
};

const EquipesProdutividadeTable = ({
  periodoSelecionado,
  contrato,
  centro,
  equipe,
  startDate,
  endDate,
  loading = false,
  refreshKey = 0
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headerBg = isDark ? '#153444' : 'grey.100';
  const rowBgEven = isDark ? 'rgba(255, 255, 255, 0.04)' : 'background.default';
  const rowBgOdd = isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.50';
  const rowBgEvenCentro = isDark ? 'rgba(255, 255, 255, 0.02)' : 'grey.50';
  const rowBgOddCentro = isDark ? 'rgba(255, 255, 255, 0.06)' : 'grey.100';
  const bgColorCentro = isDark ? 'rgba(255, 255, 255, 0.05)' : 'grey.50';
  const bgColorEquipe = isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.100';

  const [loadingData, setLoadingData] = useState(false);
  const [dadosPorContrato, setDadosPorContrato] = useState({});
  const [contratosList, setContratosList] = useState([]);
  const [expandedContratos, setExpandedContratos] = useState(new Set());
  const [expandedCentros, setExpandedCentros] = useState(new Set());

  const meses = useMemo(() => calcularMeses(periodoSelecionado), [periodoSelecionado]);

  useEffect(() => {
    if (!contrato?.length || !centro?.length || !equipe?.length) {
      setDadosPorContrato({});
      setContratosList([]);
      return;
    }

    const fetchData = async () => {
      setLoadingData(true);
      const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

      try {
        const resp = await fetch(`${apiUrl}/produtividade-agrupada-por-periodo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'data-referencia-inicio': fmt(startOfMonth(meses[0])),
            'data-referencia-fim':    fmt(endOfMonth(meses[meses.length - 1])),
            'des_contrato':       Array.isArray(contrato) ? contrato : [contrato],
            'des_centro_servico': Array.isArray(centro)   ? centro   : [centro],
            'des_equipe':         Array.isArray(equipe)   ? equipe   : [equipe]
          })
        });

        const data = await resp.json();

        // Mapear resposta da API para o shape de estado esperado pelo JSX:
        // dadosPorContrato[contrato] = { centros[], medias{}, centrosData{ centro: { equipes[], equipesData{}, medias{} } } }
        const novoDadosPorContrato = {};
        const novoContratosList = [];

        (data.contratos || []).forEach(c => {
          novoContratosList.push(c.contrato);
          const centrosData = {};
          (c.centros || []).forEach(ct => {
            const equipesData = {};
            (ct.equipes || []).forEach(eq => { equipesData[eq.equipe] = eq.valores; });
            centrosData[ct.centro] = {
              equipes: ct.equipes.map(e => e.equipe),
              equipesData,
              medias: ct.medias
            };
          });
          novoDadosPorContrato[c.contrato] = {
            centros: c.centros.map(ct => ct.centro),
            medias:  c.medias,
            centrosData
          };
        });

        setDadosPorContrato(novoDadosPorContrato);
        setContratosList(novoContratosList);
      } catch (error) {
        console.error('Erro ao buscar dados de produtividade:', error);
        setDadosPorContrato({});
        setContratosList([]);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [meses, contrato, centro, equipe, refreshKey]);

  if (loading || loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const toggleContrato = (contratoNome) => {
    setExpandedContratos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contratoNome)) {
        newSet.delete(contratoNome);
      } else {
        newSet.add(contratoNome);
      }
      return newSet;
    });
  };

  const toggleCentro = (contratoNome, centroNome) => {
    const key = `${contratoNome}|${centroNome}`;
    setExpandedCentros(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  if (contratosList.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography color="text.secondary">
          Nenhum dado encontrado para o período selecionado.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 3, 
        boxShadow: 3, 
        mt: 3,
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: 600,
        },
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          Produtividade por Equipe - Período Selecionado
        </Typography>
      </Box>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: headerBg }}>
            <TableCell sx={{ 
              fontWeight: 700, 
              position: 'sticky', 
              left: 0, 
              backgroundColor: headerBg, 
              zIndex: 10, 
              minWidth: { xs: 150, sm: 200 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            }}>
              Contrato / Centro / Equipe
            </TableCell>
            {meses.map((mes) => {
              const mesKey = format(mes, 'yyyy-MM');
              const mesNome = format(mes, 'MMMM yyyy', { locale: ptBR });
              return (
                <TableCell
                  key={mesKey}
                  align="center"
                  sx={{ 
                    fontWeight: 700, 
                    minWidth: { xs: 100, sm: 120 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {mesNome.charAt(0).toUpperCase() + mesNome.slice(1)}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {contratosList.map((contratoNome, contratoIdx) => {
            const contratoData = dadosPorContrato[contratoNome];
            const isExpanded = expandedContratos.has(contratoNome);
            
            if (!contratoData) return null;

            return (
              <React.Fragment key={contratoNome}>
                {/* Linha do Contrato (com média) */}
                <TableRow
                  sx={{
                    backgroundColor: contratoIdx % 2 === 0 ? rowBgEven : rowBgOdd,
                    '&:hover': { backgroundColor: 'action.hover' },
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleContrato(contratoNome)}
                >
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      position: 'sticky',
                      left: 0,
                      backgroundColor: contratoIdx % 2 === 0 ? rowBgEven : rowBgOdd,
                      zIndex: 9,
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleContrato(contratoNome);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        {isExpanded ? <ExpandMore /> : <ChevronRight />}
                      </IconButton>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {contratoNome}
                      </Typography>
                    </Box>
                  </TableCell>
                  {meses.map((mes) => {
                    const mesKey = format(mes, 'yyyy-MM');
                    const valor = contratoData.medias[mesKey];
                    const valorFormatado = valor !== null && valor !== undefined && !isNaN(valor)
                      ? Number(valor).toFixed(1)
                      : '--';

                    return (
                      <TableCell
                        key={`${contratoNome}-${mesKey}`}
                        align="center"
                        sx={{
                          backgroundColor: getCellBackgroundColor(valor),
                          fontWeight: 700,
                          fontSize: '0.9rem'
                        }}
                      >
                        {valor !== null && valor !== undefined && !isNaN(valor) ? (
                          <Chip
                            label={`${valorFormatado}%`}
                            color={getPercentualColor(valor)}
                            size="small"
                            sx={{ fontWeight: 700, minWidth: 70 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {valorFormatado}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
                
                {/* Linhas dos Centros (quando contrato expandido) */}
                {isExpanded && contratoData.centros.map((centroNome, centroIdx) => {
                  const centroKey = `${contratoNome}|${centroNome}`;
                  const isCentroExpanded = expandedCentros.has(centroKey);
                  const centroData = contratoData.centrosData[centroNome];
                  const bgColorCentro = centroIdx % 2 === 0 ? rowBgEvenCentro : rowBgOddCentro;
                  
                  return (
                    <React.Fragment key={`${contratoNome}-${centroNome}`}>
                      {/* Linha do Centro */}
                      <TableRow
                        sx={{
                          backgroundColor: bgColorCentro,
                          '&:hover': { backgroundColor: 'action.hover' },
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleCentro(contratoNome, centroNome)}
                      >
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            position: 'sticky',
                            left: 0,
                            backgroundColor: bgColorCentro,
                            zIndex: 8,
                            pl: 4,
                            '&:hover': { backgroundColor: 'action.hover' }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCentro(contratoNome, centroNome);
                              }}
                              sx={{ p: 0.5 }}
                            >
                              {isCentroExpanded ? <ExpandMore /> : <ChevronRight />}
                            </IconButton>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {centroNome}
                            </Typography>
                          </Box>
                        </TableCell>
                        {meses.map((mes) => {
                          const mesKey = format(mes, 'yyyy-MM');
                          const valor = centroData.medias[mesKey];
                          const valorFormatado = valor !== null && valor !== undefined && !isNaN(valor)
                            ? Number(valor).toFixed(1)
                            : '--';

                          return (
                            <TableCell
                              key={`${contratoNome}-${centroNome}-${mesKey}`}
                              align="center"
                              sx={{
                                backgroundColor: getCellBackgroundColor(valor),
                                fontWeight: 700,
                                fontSize: '0.9rem'
                              }}
                            >
                              {valor !== null && valor !== undefined && !isNaN(valor) ? (
                                <Chip
                                  label={`${valorFormatado}%`}
                                  color={getPercentualColor(valor)}
                                  size="small"
                                  sx={{ fontWeight: 700, minWidth: 70 }}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {valorFormatado}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      
                      {/* Linhas das Equipes (quando centro expandido) */}
                      {isCentroExpanded && centroData.equipes.map((equipeNome, equipeIdx) => {
                        const bgColorEquipe = equipeIdx % 2 === 0 ? bgColorCentro : rowBgOddCentro;
                        return (
                          <TableRow
                            key={`${contratoNome}-${centroNome}-${equipeNome}`}
                            sx={{
                              backgroundColor: bgColorEquipe,
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: bgColorEquipe,
                                zIndex: 7,
                                pl: 8,
                                '&:hover': { backgroundColor: 'action.hover' }
                              }}
                            >
                              {equipeNome}
                            </TableCell>
                            {meses.map((mes) => {
                              const mesKey = format(mes, 'yyyy-MM');
                              const valor = centroData.equipesData[equipeNome]?.[mesKey];
                              const valorFormatado = valor !== null && valor !== undefined && !isNaN(valor)
                                ? Number(valor).toFixed(1)
                                : '--';

                              return (
                                <TableCell
                                  key={`${contratoNome}-${centroNome}-${equipeNome}-${mesKey}`}
                                  align="center"
                                  sx={{
                                    backgroundColor: getCellBackgroundColor(valor),
                                    fontWeight: 500,
                                    fontSize: '0.9rem'
                                  }}
                                >
                                  {valor !== null && valor !== undefined && !isNaN(valor) ? (
                                    <Chip
                                      label={`${valorFormatado}%`}
                                      color={getPercentualColor(valor)}
                                      size="small"
                                      sx={{ fontWeight: 700, minWidth: 70 }}
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      {valorFormatado}
                                    </Typography>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default EquipesProdutividadeTable;

